/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AuthContext } from './Auth/AuthContext';
import usePlans from '../hooks/usePlans';
import { getCompany } from '../services/userService';
import { listCompanyApps } from '../services/appsService';
import { getAiCredits } from '../services/aiCreditsService';
import { TRIAL_EXPIRING_SOON_DAYS } from '../types/company';

// platformProviders (openai/gemini habilitados na plataforma) é config global que
// muda raríssimo. O loadPlan re-roda a cada mudança de user.trial/company, então
// memoizamos por sessão para não refazer GET /ai/credits a cada re-execução.
let _platformProvidersCache = null;
let _platformProvidersPromise = null;

const fetchPlatformProviders = () => {
  if (_platformProvidersCache) return Promise.resolve(_platformProvidersCache);
  if (_platformProvidersPromise) return _platformProvidersPromise;
  _platformProvidersPromise = getAiCredits()
    .then(({ data }) => {
      _platformProvidersCache = data?.platformProviders ?? {};
      return _platformProvidersCache;
    })
    .catch(() => ({}))
    .finally(() => {
      _platformProvidersPromise = null;
    });
  return _platformProvidersPromise;
};

const defaultState = {
  loading: true,
  planActive: true,
  // trial — derivado de user.trial (SerializeUser do backend)
  trialActive: false,
  trialDaysLeft: 0,
  trialExpiresAt: null,
  trialExpiringSoon: false,
  // features do plano
  campaigns: false,
  schedules: false,
  openAi: false,
  internalChat: false,
  integrations: false,
  whatsappOfficial: false,
  facebook: false,
  instagram: false,
  tasks: false,
  products: false,
  // Clientes não é mais gated por plano — sempre disponível
  clients: true,
  // modos de IA disponíveis no plano
  allowAiByok: false,
  allowAiPlatformCredits: false,
  // providers configurados na plataforma (chaves salvas pelo super admin)
  platformOpenAi: false,
  platformGemini: false,
  // apps da empresa — slugs dos apps ativos (status=active, sem accessBlocked)
  activeAppSlugs: new Set(),
};

const PLAN_PERMISSIONS_CACHE_PREFIX = 'plan_permissions_v1';

const toCachePayload = (state) => ({
  ...state,
  activeAppSlugs: Array.from(state?.activeAppSlugs || []),
});

const fromCachePayload = (payload) => ({
  ...defaultState,
  ...payload,
  loading: false,
  activeAppSlugs: new Set(payload?.activeAppSlugs || []),
});

const readCachedPermissions = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return fromCachePayload(parsed);
  } catch {
    return null;
  }
};

const writeCachedPermissions = (cacheKey, state) => {
  if (!cacheKey) return;
  try {
    localStorage.setItem(cacheKey, JSON.stringify(toCachePayload(state)));
  } catch {
    // silent
  }
};

const PlanPermissionsContext = createContext({
  ...defaultState,
  canAccess: () => false,
  hasApp: () => false,
  refetchPlanPermissions: () => Promise.resolve(),
});

/**
 * Deriva o estado de trial diretamente de user.trial (retornado por /auth/me).
 * Não depende mais de plan.trialDays nem de cálculos locais de dueDate.
 */
const extractTrialState = (userTrial) => {
  if (!userTrial || typeof userTrial !== 'object') {
    return {
      trialActive: false,
      trialDaysLeft: 0,
      trialExpiresAt: null,
      trialExpiringSoon: false,
    };
  }

  const active = Boolean(userTrial.inTrial);
  const daysLeft = typeof userTrial.daysRemaining === 'number' ? userTrial.daysRemaining : 0;
  const expiresAt = userTrial.expiresAt ?? null;
  const expiringSoon = active && daysLeft <= TRIAL_EXPIRING_SOON_DAYS;

  return {
    trialActive: active,
    trialDaysLeft: daysLeft,
    trialExpiresAt: expiresAt,
    trialExpiringSoon: expiringSoon,
  };
};

/**
 * Determina se a conta está ativa.
 *
 * company.status (boolean) — desativação manual pelo super admin ou pelo cron
 * de inadimplência (dueDate vencida há 4+ dias em plano pago).
 *
 * user.trial.expired — trial venceu sem contratação de plano. O backend já
 * trata conversão: plano ativado durante o trial (trialEndedAt preenchido)
 * retorna expired=false.
 */
const derivePlanActive = (isSuperAdmin, company, userTrial) => {
  if (isSuperAdmin) return true;
  if (company?.status === false) return false;
  if (userTrial?.expired === true) return false;
  return true;
};

/**
 * Plano já escolhido/em uso (mesmo em período de teste): permissões seguem o plano.
 * Alinha com Planos/index.jsx — /auth/me e listPlan podem expor id em `plan`, `company.planId` ou `company.plan.id`.
 */
const hasCompanyPlanInUse = (plan, company) => {
  const id = plan?.id ?? company?.planId ?? company?.plan?.id ?? null;
  if (id === null || id === undefined) return false;
  if (typeof id === 'string' && id.trim() === '') return false;
  return true;
};

export const PlanPermissionsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const cacheKey = useMemo(() => {
    if (!user?.companyId) return null;
    return `${PLAN_PERMISSIONS_CACHE_PREFIX}:${user.companyId}:${user?.id || 'anon'}`;
  }, [user?.companyId, user?.id]);

  const [state, setState] = useState(defaultState);
  /** Incrementa para forçar novo carregamento (ex.: após ativar/desativar app na loja). */
  const [reloadTick, setReloadTick] = useState(0);
  const refetchResolveRef = useRef(null);

  const refetchPlanPermissions = useCallback(() => {
    return new Promise((resolve) => {
      refetchResolveRef.current = resolve;
      setReloadTick((t) => t + 1);
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      try {
        const isSuperAdmin = Boolean(user?.super);
        const cachedState = readCachedPermissions(cacheKey);
        if (cachedState && isMounted) {
          setState((prev) => (prev.loading ? cachedState : prev));
        }

        if (!user?.companyId) {
          const nextState = {
            ...defaultState,
            loading: false,
            campaigns: isSuperAdmin,
            schedules: isSuperAdmin,
            openAi: isSuperAdmin,
            internalChat: isSuperAdmin,
            integrations: isSuperAdmin,
            whatsappOfficial: isSuperAdmin,
            facebook: isSuperAdmin,
            instagram: isSuperAdmin,
            allowAiByok: isSuperAdmin,
            allowAiPlatformCredits: isSuperAdmin,
            platformOpenAi: isSuperAdmin,
            platformGemini: isSuperAdmin,
          };
          setState(() => nextState);
          writeCachedPermissions(cacheKey, nextState);
          return;
        }

        const isRegularUser = !user?.super && user?.profile === 'user';

        try {
          const [planConfigs, companyRes, companyAppsData, platformProvidersData] =
            await Promise.all([
              getPlanCompany(undefined, user.companyId).catch(() => null),
              isRegularUser
                ? Promise.resolve({ data: user?.company ?? null })
                : getCompany(user.companyId).catch(() => ({ data: null })),
              isRegularUser ? Promise.resolve([]) : listCompanyApps(user.companyId).catch(() => []),
              fetchPlatformProviders(),
            ]);
          const platformProviders = platformProvidersData ?? {};
          const platformOpenAi = Boolean(platformProviders.openai);
          const platformGemini = Boolean(platformProviders.gemini);
          if (!isMounted) return;

          const plan = planConfigs?.plan || {};
          const company = companyRes?.data || user?.company;
          const companyApps = Array.isArray(companyAppsData) ? companyAppsData : [];
          const activeAppSlugs = isRegularUser
            ? new Set(Array.isArray(user?.activeApps) ? user.activeApps : [])
            : new Set(
                companyApps
                  .filter((ca) => ca.status === 'active' && !ca.accessBlocked)
                  .map((ca) => ca.app?.slug || ca.appExtension?.slug)
                  .filter(Boolean)
              );

          const planActive = derivePlanActive(isSuperAdmin, company, user?.trial);
          const trialState = extractTrialState(user?.trial);

          // Com plano em uso (id no payload do plano ou em company), o banner de trial some e as flags do plano valem,
          // mesmo com inTrial=true no backend.
          const planInUse = hasCompanyPlanInUse(plan, company);
          const effectiveTrialActive = trialState.trialActive && !planInUse;
          const adjustedTrialState = { ...trialState, trialActive: effectiveTrialActive };
          // Trial sem plano escolhido: libera flags do plano (canais, IA, etc.); apps seguem só os ativos na empresa.
          const unlockPlanFeatureGates = isSuperAdmin || effectiveTrialActive;

          if (planConfigs?.plan) {
            const authPlan = user?.company?.plan || {};
            const feat = (key) =>
              unlockPlanFeatureGates || Boolean(plan[key]) || Boolean(authPlan[key]);
            const nextState = {
              loading: false,
              planActive,
              ...adjustedTrialState,
              campaigns: feat('useCampaigns'),
              schedules: feat('useSchedules'),
              openAi: feat('useOpenAi'),
              internalChat: feat('useInternalChat'),
              integrations: feat('useIntegrations'),
              whatsappOfficial: feat('useWhatsappOfficial'),
              facebook: feat('useFacebook'),
              instagram: feat('useInstagram'),
              tasks: feat('useTasks'),
              products: feat('useProducts'),
              clients: true,
              allowAiByok: feat('allowAiByok'),
              allowAiPlatformCredits: feat('allowAiPlatformCredits'),
              platformOpenAi,
              platformGemini,
              activeAppSlugs,
            };
            setState(nextState);
            writeCachedPermissions(cacheKey, nextState);
          } else {
            const gatesNoPlanResponse = isSuperAdmin || adjustedTrialState.trialActive;
            const authPlan = user?.company?.plan || {};
            const ff = (key) => gatesNoPlanResponse || Boolean(authPlan[key]);
            const nextState = {
              ...defaultState,
              loading: false,
              planActive,
              ...adjustedTrialState,
              campaigns: ff('useCampaigns'),
              schedules: ff('useSchedules'),
              openAi: ff('useOpenAi'),
              internalChat: ff('useInternalChat'),
              integrations: ff('useIntegrations'),
              whatsappOfficial: ff('useWhatsappOfficial'),
              facebook: ff('useFacebook'),
              instagram: ff('useInstagram'),
              tasks: ff('useTasks'),
              products: ff('useProducts'),
              allowAiByok: ff('allowAiByok'),
              allowAiPlatformCredits: ff('allowAiPlatformCredits'),
              platformOpenAi,
              platformGemini,
              activeAppSlugs,
            };
            // Distingue "backend offline" (plan + company falharam juntos) de
            // "sem plano cadastrado" (usuário em trial, company retornou dados).
            // Só preserva o estado existente quando há evidência de falha de rede.
            const isNetworkError = planConfigs === null && companyRes?.data === null;
            setState((prev) => {
              if (isNetworkError && !prev.loading) return { ...prev, loading: false };
              return nextState;
            });
            if (!isNetworkError) {
              writeCachedPermissions(cacheKey, nextState);
            }
          }
        } catch (err) {
          if (isMounted) {
            const isSuperAdmin = Boolean(user?.super);
            const authPlan = user?.company?.plan || {};
            const ff = (key) => isSuperAdmin || Boolean(authPlan[key]);
            setState((prev) => {
              // Em falhas transitórias de rede/backend, mantém permissões já conhecidas
              // para evitar "sumiço" momentâneo de features na UI.
              if (!prev.loading) {
                return {
                  ...prev,
                  loading: false,
                };
              }

              return {
                ...defaultState,
                loading: false,
                campaigns: ff('useCampaigns'),
                schedules: ff('useSchedules'),
                openAi: ff('useOpenAi'),
                internalChat: ff('useInternalChat'),
                integrations: ff('useIntegrations'),
                whatsappOfficial: ff('useWhatsappOfficial'),
                facebook: ff('useFacebook'),
                instagram: ff('useInstagram'),
                tasks: ff('useTasks'),
                products: ff('useProducts'),
                allowAiByok: ff('allowAiByok'),
                allowAiPlatformCredits: ff('allowAiPlatformCredits'),
              };
            });
          }
        }
      } finally {
        const resolveRefetch = refetchResolveRef.current;
        refetchResolveRef.current = null;
        resolveRefetch?.();
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
    };
  }, [
    user?.companyId,
    user?.company?.status,
    user?.company?.dueDate,
    user?.company?.planId,
    user?.company?.plan?.id,
    user?.company?.plan?.allowAiByok,
    user?.company?.plan?.allowAiPlatformCredits,
    user?.trial,
    user?.super,
    reloadTick,
    cacheKey,
  ]);

  const value = useMemo(() => {
    const isSuperAdmin = Boolean(user?.super);

    const canAccess = (featureKey) => {
      if (isSuperAdmin) return true;
      if (state.trialActive) return true;
      if (!featureKey) return true;
      return Boolean(state[featureKey]);
    };

    // Apps: sempre pela loja/instalação (activeAppSlugs); trial não libera app não contratado.
    const hasApp = (slug) => isSuperAdmin || state.activeAppSlugs.has(slug);

    return { ...state, canAccess, hasApp, refetchPlanPermissions };
  }, [state, user?.super, refetchPlanPermissions]);

  return (
    <PlanPermissionsContext.Provider value={value}>{children}</PlanPermissionsContext.Provider>
  );
};

export const usePlanPermissions = () => {
  return useContext(PlanPermissionsContext);
};

export default PlanPermissionsContext;
